import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

type AuthHandlers = {
    bearer: (token: string | null, config: InternalAxiosRequestConfig) => void;
    apiKey: (token: string | null, config: InternalAxiosRequestConfig, headerName?: string) => void;
    basic: (username: string, password: string, instance: AxiosInstance) => void;
};

const authHandlers: AuthHandlers = {
    bearer: (token, config) => {
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    },
    apiKey: (token, config, headerName = 'X-API-Key') => {
        if (token) {
            config.headers[headerName] = token;
        }
    },
    basic: (username, password, instance) => {
        instance.defaults.auth = { username, password };
    },
};

type AuthOption =
    | { type: 'bearer'; getToken: () => string | null }
    | { type: 'apiKey'; headerName?: string; getToken: () => string | null }
    | { type: 'basic'; username: string; password: string };

interface CreateAxiosOptions {
    baseURL: string;
    timeout?: number;
    auth?: AuthOption;
}

export function createAxiosInstance({ baseURL, timeout = 10000, auth }: CreateAxiosOptions): AxiosInstance {
    const instance = axios.create({ baseURL, timeout });

    if (auth) {
        switch (auth.type) {
            case 'basic':
                authHandlers.basic(auth.username, auth.password, instance);
                break;

            case 'bearer':
            case 'apiKey':
                instance.interceptors.request.use((config) => {
                    if (auth.type === 'bearer') {
                        authHandlers.bearer(auth.getToken(), config);
                    } else {
                        authHandlers.apiKey(auth.getToken(), config, auth.headerName);
                    }
                    return config;
                });
                break;
        }
    }

    instance.interceptors.response.use(
        (res) => res.data,
        (err: AxiosError) => Promise.reject(err)
    );

    return instance;
}
